export const RISK_LEVELS = [
  {
    name: "Extreme",
    value: 5,
    bgColor: "#C10000",
    fontColor: "#FFFFFF",
  },
  {
    name: "High",
    value: 4,
    bgColor: "#FF6700",
    fontColor: "#000000",
  },
  {
    name: "Medium",
    value: 3,
    bgColor: "#FFC600",
    fontColor: "#000000",
  },
  {
    name: "Low",
    value: 2,
    bgColor: "#ADD600",
    fontColor: "#000000",
  },
  {
    name: "Very Low",
    value: 1,
    bgColor: "#4DC400",
    fontColor: "#000000",
  },
];

export const COUNTRY_PROFILES = {
  political: {
    name: "Political system",
  },
  economic: {
    name: "Economy",
  },
};

export const MARITIME_MAP_TILE_LAYER_INFO = {
  layerName: "MaritimeNov22",
  tilesetId: "batdev.8h8yp6zt",
  type: "water",
};

export const RISK_RATING_PRIMARIES = {
  political: "dd8fac8d-6f95-41e5-9f8e-9878037a2969",
  operational: "a4d0c0e0-5eb3-4fc1-9ffc-da8072c3dbbe",
  security: "a19bfe35-22d5-4361-99d6-2f65fc7df184",
  maritime: "d0141e74-32e6-41f8-ae91-42eef95e4fe4",
  cyber: "53bd26cf-58fb-4ce9-8d04-e239f40d6710",
};

export const TRAVEL_RISK_RATING_ID = "51134a2b-6d0e-4d96-af46-31c0ffed9439";

export const RISK_RATING_SUB = {
  kidnap: "8b3def4a-44e3-473a-9cc9-79a2e2539311",
};

export const RISK_RATING_DESCRIPTIONS = [
  {
    id: RISK_RATING_PRIMARIES.political,
    name: "Political Risk",
    description:
      "Political risk evaluates the likelihood of government interference and political instability, and their impact on the business environment. Political risk assesses general political stability and policy issues such as regulatory change, high-level corruption, reputational risk, expropriation and nationalisation, contractual interference, sovereign default and non-payment, and international sanctions. While the rating applies generally to the designated jurisdiction, political risk may vary considerably between industry sectors and investor nationalities.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "The political and policy environments present sustained critical challenges to business. For example:",
        details: [
          "Political instability precludes routine business operations outside certain enclaves.",
          "The government is fundamentally unstable and vulnerable to unconstitutional regime change, including military coup d’état.",
          "Business activities are subject to state predation through expropriation and other confiscatory actions.",
          "Systemic political instability precludes policymaking or contractual stability.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "The political and policy environments are persistently challenging for business. For example:",
        details: [
          "Political instability often impedes routine business operations.",
          "Political transitions are typically contested, threatened by social unrest, or prone to delay or disruption.",
          "The government may be hostile to foreign companies and actively interferes with business operations.",
          "Policymaking is generally opaque, often capricious or erratic, and strongly influenced by vested interests.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "The political and policy environments are periodically challenging for business. For example:",
        details: [
          "Political instability occasionally impedes business operations.",
          "Transitions of power may be contested or threatened by social unrest.",
          "The government limits international investment to certain sectors, and displays a willingness to politicise or interfere in contracts.",
          "Policymaking is occasionally opaque or capricious, and often influenced by vested interests.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "The political and policy environments are broadly favourable for business. For example:",
        details: [
          "The government is generally stable, and sporadic political instability (e.g. resulting from minority or coalition government) does not significantly impede business.",
          "Transitions of power are generally peaceful, institutional and broadly perceived as legitimate.",
          "The government is generally friendly towards international investment, but the government or other political actors may occasionally put political pressure on companies.",
          "Policymaking is generally transparent and predictable, but may be occasionally influenced by vested interests.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "The political and policy environments are favourable for business. For example:",
        details: [
          "The government is fundamentally stable.",
          "Transitions of power are peaceful, institutional and perceived as legitimate.",
          "The government welcomes international investment and investor rights are strongly protected by law and international agreements.",
          "Policymaking is transparent and predictable.",
        ],
      },
    ],
  },
  {
    id: "a75f55f3-e351-48b1-85bb-da464a00dd51",
    name: "Political Stability Risk",
    description:
      "Political stability risk evaluates the impact of political instability on business operations. It assesses the likelihood of the government completing its expected or appointed term; levels of political polarisation; the threat of unconstitutional change of government; the role and influence of non-government political actors; and the degree of political repression.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Political instability poses sustained critical challenges to business. For example:",
        details: [
          "Government is unable to function reliably, including due to war or civil conflict, and its reach and legitimacy are limited to fractions of the country. Local power is primarily exercised by informal authorities.",
          "Political competition occurs largely among factional elites and is often characterised by political violence, precluding political participation by many constituencies.",
          "Government transitions may be rejected as illegitimate by large parts of the population or country and cause significant disruption to business, and there is high potential for unplanned or unconstitutional regime change (including through foreign military intervention).",
          "Political freedoms and civil rights are unprotected and unrealised; civil society is virtually non-existent.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Political instability poses persistent and serious challenges to business. For example:",
        details: [
          "Government is largely unaccountable and may be ineffective, with political authority contested among competing power centres, potentially including the military and security services.",
          "Political competition is mostly informal, may be largely conducted among factional elites and often affects the business sphere. Political repression and disenfranchisement, including through systematic violence, may also be present.",
          "Government transitions may be regularly challenged, and often cause disruption to normal business. There is high potential for unplanned or unconstitutional regime change.",
          "Political freedoms are limited and civil rights unprotected; civil society is minimal and there are few outlets for political participation.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Political instability periodically poses challenges to business. For example:",
        details: [
          "Government may at times be responsive to specific political constituencies (e.g. ethno-national or religious groups) and power may be exercised informally (e.g. through patronage networks).",
          "Political competition is conducted without full regard to constitutional norms (or succession processes are opaque and unpredictable) and occasionally affects the business sphere; some political constituencies may be disenfranchised.",
          "Government transitions may be challenged, and sometimes cause disruption to business. There is low potential for unplanned or unconstitutional regime change.",
          "Political freedoms are circumscribed and civil rights occasionally violated; civil society operates with restrictions (e.g. on leadership, funding, activities).",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Political instability rarely affects business. For example:",
        details: [
          "Government is generally responsive and accountable, but may occasionally favour certain domestic constituencies.",
          "Political competition is conducted according to settled constitutional norms, deliberate and predictable succession processes, or accepted formal or informal arrangements, and does not play out in the business sphere.",
          "Government transitions are generally peaceful and mostly perceived as legitimate, causing little disruption to business. There is negligible potential for unplanned or unconstitutional regime change.",
          "Accepted political freedoms and civil rights are protected for most domestic constituencies, and civil society operates with few restrictions.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight: "The political environment is stable. For example:",
        details: [
          "Government is open, transparent, responsive and accountable.",
          "Political competition is conducted according to settled constitutional norms and established institutional arrangements.",
          "Government transitions are democratic, peaceful and perceived as legitimate, and cause no business disruption.",
          "Universal political freedoms and civil rights are strongly protected, and civil society operates with no significant restrictions.",
        ],
      },
    ],
  },
  {
    id: "0af9a5ad-7733-4f9f-b3ce-707e7221f096",
    name: "Regulatory Risk",
    description:
      "Regulatory risk evaluates the development, implementation, stability and enforcement of regulation that impacts the business environment. Factors assessed include the stability of the regulatory environment and potential for abrupt regulatory change; the transparency and openness of the process for developing regulation; the influence of vested or special interests over regulation; the independence of regulatory agencies; and patterns of regulatory enforcement, including selective enforcement for political or other reasons.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Regulation of business is highly capricious and dominated by vested or political interests. For example:",
        details: [
          "The regulatory environment is unpredictable and broadly incoherent, with major inconsistencies between regulatory agencies or authorities.",
          "Regulatory change is abrupt or capricious, and generally determined according to informal, opaque or dictatorial processes.",
          "Vested, special or political interests dominate regulation, with little or no opportunity for or consideration of independent business or civil society input.",
          "Regulatory enforcement is highly politicised, capricious and inconsistent, often seeking to benefit vested interests to the detriment of foreign companies.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Regulation of business is politicised and strongly influenced by vested interests. For example:",
        details: [
          "The regulatory environment is generally unpredictable, with significant overlaps or inconsistencies between regulatory agencies or authorities.",
          "Regulatory change is frequently abrupt or capricious, and often determined according to informal and opaque processes.",
          "Businesses and civil society have few opportunities to provide input on regulation, and vested or special interests have strong influence.",
          "Regulatory enforcement is often politicised or capricious, with significant inconsistencies and strong prejudice against foreign companies.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Regulation of business is not fully independent, and enforcement is sometimes applied inconsistently or capriciously. For example:",
        details: [
          "The regulatory environment is subject to some unpredictable change in certain sectors, and there are some overlaps and inconsistencies among regulatory agencies and authorities.",
          "Regulatory change is occasionally abrupt, including as a function of executive prerogative, and formal political and administrative processes can be unclear, opaque or circumvented.",
          "Businesses and civil society have limited opportunity to provide input on regulation, and vested or special interests are influential in some areas.",
          "Regulatory enforcement is occasionally unpredictable, capricious or politicised, with inconsistencies between sectors or regions, and potential prejudice against foreign companies.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Regulation of business is generally independent and stable, but enforcement varies by sector or region. For example:",
        details: [
          "The regulatory environment is largely predictable and coherent.",
          "Regulatory change is mostly well considered and undertaken according to established but occasionally unclear political and administrative processes, with limited potential for abrupt or executive change.",
          "Business and civil society have sufficient opportunity to provide input on regulation, which is usually given impartial consideration.",
          "Regulatory enforcement is generally predictable and impartial, but may be inconsistently applied in different sectors or regions.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "Regulation of business is independent, stable and predictably enforced. For example:",
        details: [
          "The regulatory environment is stable and coherent.",
          "Regulatory change is undertaken according to clear, well-defined political and administrative processes.",
          "Business and civil society have ample opportunity to provide input on regulation, which is typically given formal and impartial consideration.",
          "Regulatory enforcement is predictable, consistent, transparent and impartial.",
        ],
      },
    ],
  },
  {
    id: "3611b6c4-ec68-4d64-8607-709b331e407a",
    name: "Contract Risk",
    description:
      "Contract risk evaluates the predictability, durability, legality and legitimacy of commercial contracts signed in the jurisdiction, especially between foreign companies and the government or state-owned entities. It addresses the likelihood that a government will interfere in a contract (e.g. by altering terms or preventing the execution of a contract); the likelihood that political circumstances will call into question the legality and legitimacy of contracts (e.g. over territorial disputes, corruption or elections); the likelihood of non-payment; and the likelihood that the government will review and potentially cancel or revise operating or ownership licences (i.e. oil and gas or minerals) granted to companies.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Contract frustration poses sustained critical challenges for business. For example:",
        details: [
          "Gross mismanagement and political interference prevent or delay the fulfilment of many contracts involving the government or state-controlled companies.",
          "Non-payment, non-delivery and licence cancellation without cause are common.",
          "The judiciary is highly politicised and reliably abrogates contractual rights in favour of the government or politically-connected companies. There is no effective recourse to international arbitration.",
          "The country may be subject to comprehensive international trade and financial sanctions that prevent the fulfilment of contractual obligations or require the termination of contracts in most sectors.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Contract frustration poses persistent and serious challenges for business. For example:",
        details: [
          "The government periodically revises or interferes in contracts, or conducts reviews of operating licences.",
          "Non-payment, non-delivery and licence cancellation without cause occur frequently.",
          "The judiciary is politicised, capricious or lacks sufficient independence to safeguard contractual rights, and there is little recourse to international arbitration.",
          "The country may be subject to international trade and financial sanctions that prevent the fulfilment of contractual obligations or require the termination of contracts in key sectors.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Contract frustration periodically poses challenges for business. For example:",
        details: [
          "The government may occasionally revise or interfere in contracts.",
          "Non-payment, non-delivery and licence cancellation without cause occur intermittently.",
          "Redress to compensation through the domestic legal system is difficult or time-consuming; international arbitration decisions are difficult to enforce.",
          "Individuals in the country may be subject to targeted sanctions that could impede fulfilment of contracts.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Contract frustration is rare and poses a limited threat to business. For example:",
        details: [
          "There is little prospect of the government retrospectively revising or interfering in contracts.",
          "Non-payment, non-delivery and licence cancellation without cause occur only rarely.",
          "Contractual disputes and compensation are resolved through a broadly well-functioning, independent judiciary; international arbitration is generally enforced.",
          "The country is not subject to trade or financial sanctions that would impede the fulfilment of contractual obligations.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "Contract frustration does not affect business. For example:",
        details: [
          "There is no prospect of the government retrospectively revising or interfering in contracts.",
          "Commercial contracts are generally honoured by government  and private parties. ",
          "Contractual disputes are resolved through a well-functioning, independent judiciary; international arbitration decisions are enforced in local courts.",
        ],
      },
    ],
  },
  {
    id: "d54a3999-ed0d-4c41-824e-913c86159ca5",
    name: "Integrity Risk",
    description:
      "Integrity risk evaluates the ability of companies to operate in compliance with international norms, responsible business practices and corporate ethical standards. It encompasses the prevalence and threat of corruption (public and private) to routine and legitimate business activities; the nature and enforcement of anti-corruption policy; the transparency and fairness of contract and licence awards; the availability, accessibility and transparency of corporate records; and the nature and severity of human rights, environmental and other reputational concerns. ",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Integrity risks severely limit investment or operations in most sectors and areas of the country. For example:",
        details: [
          "Corruption is systemic in the business environment. It is extremely difficult to do business without paying bribes or establishing special relationships with senior individuals in the public and private sectors.",
          "The government is involved or complicit with corrupt business practices and has little interest or incentive to limit corruption. Private companies do not have anti-corruption programmes.",
          "A process for awarding contracts and licences does not exist or is dysfunctional, and awards require special relationships with senior government officials.",
          "Corporate records are not publically or easily accessible, lack substantial amounts of important data, and may be fraudulent or inaccurate.",
          "Gross human rights violations and/or severe environmental degradation occur frequently.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Integrity risks greatly hinder ethical investment or operations in some sectors and areas of the country. For example:",
        details: [
          "Corruption is prevalent in key sectors. Most companies face regular demands for bribes and improper relationships, including at senior levels of private or public sectors.",
          "The government is not publicly committed to combating corruption and does not act to deter or restrict corruption. Any enforcement is likely to be used selectively for political purposes. Private companies do not have or not enforce anti-corruption programmes.",
          "Established tender processes for awarding contracts and licences are often subverted by cronyism, nepotism, or other political relationships.",
          "Corporate records are costly and cumbersome to access, require registration or payment of informal fees, and lack substantial amounts of important data.",
          "Human rights violations and/or environmental degradation are significant issues.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Companies must invest time and effort to mitigate clear or systemic integrity risks. For example:",
        details: [
          "Companies periodically face demands for low-level bribes or facilitation payments from private or public sectors, especially in selected sectors.",
          "The government is publicly committed to combating corruption, but enforcement is limited and may be selective. Private companies generally lack effective anti-corruption programmes.",
          "Established tender processes for awarding contracts and licences may be opaque and inconsistently applied, with some awards influenced by cronyism, nepotism or other political relationships.",
          "Corporate records are well-maintained, but require registration to access, may not be digitised, and may lack some important data.",
          "Human rights violations and/or environmental degradation may occur, but are effectively limited and prosecuted by the government.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Integrity risks are present in some sectors, mostly affecting small companies. For example:",
        details: [
          "Smaller companies or particular sectors may occasionally face demands for bribes or improper relationships from the private or public sectors.",
          "The government has a well-defined anti-corruption policy, but enforcement is inconsistent. Many large companies have some form of anti-corruption programme.",
          "Contracts and licences are generally awarded through open, clear and fair tender processes, with occasional instances of cronyism.",
          "Corporate records are well-maintained, available, and accessible at minimal cost, but may lack some important data.",
          "Human rights violations and environmental degradation are infrequent, limited to specific sectors or areas of the country, and swiftly halted and prosecuted by the government.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "Foreign investors and local businesses face no specific integrity issues. For example:",
        details: [
          "Businesses will not face demands for bribes, facilitation payments or relationships from the private or public sectors.",
          "The government actively enforces a well-defined anti-corruption policy, and private companies generally have mature anti-corruption programmes.",
          "Contracts and licences are awarded through open, clear and fair tender processes.",
          "Corporate records are up-to-date, complete, available, and accessible at minimal cost.",
          "Companies face almost no reputation risk linked to issues such as human rights violations and environmental degradation.",
        ],
      },
    ],
  },
  {
    id: "6dffe59e-8e19-4959-8370-bb90e00cc14e",
    name: "Sovereign Risk",
    description:
      "Sovereign risk evaluates the willingness and ability of the central government to pay its debts. Factors assessed include government ideology and policy regarding debt; attitude towards bondholders and debt holdouts; general political stability; access to international capital markets; the level and profile of government debt; and vulnerability to external shocks and financial contagion.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "The government is currently or imminently in default, and almost certain to impose losses on bondholders. For example:",
        details: [
          "Government policy and ideology repudiates foreign debt (including that incurred by a previous government). Non-payment is a significant problem. Capital controls may be in place.",
          "Central government debt is very high, and unsustainable without significant restructuring and forgiveness. The economy is in recession, with significant current exposure to internal or external shocks.",
          "Monetary and fiscal policies are dictated by political objectives and there are no/ weak professional regulatory bodies informing these decisions. National economic data does not exist or is unreliable.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Sovereign default risk is elevated and a default is likely without significant policy adjustments or external support. For example:",
        details: [
          "Government policy and ideology tolerate unsustainable fiscal deficits or the government has threatened to default on foreign debt or to revalue/devalue the currency. Non-payment or delayed payment is common.",
          "Central government debt is high, and unsustainable without significant restructuring. Growth is weak or negative, with significant exposure to internal or external shocks.",
          "Monetary and fiscal policies are often dictated by political objectives and/or regulatory bodies are subordinated to the executive, and have limited powers or capabilities. National economic data is incomplete and often unreliable.",
          "The government credit rating is below investment grade, and access to international credit markets and the IMF / other international financial institutions is conditional or suspended.",
          "The country may be undergoing a prior debt restructuring or default recovery process, or be in technical or selective default, or arrears to international creditors.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "There is a low likelihood of immediate default, but sovereign risks are likely to increase in the future. For example:",
        details: [
          "Government policy and ideology tolerate unsustainable fiscal deficits or would prioritise domestic bondholders. The government may be willing to revalue/devalue currency based on political objectives. There are periodic cases of non-payment.",
          "Central government debt is moderate to high; revenue is inadequate or unstable; and the economy is weak, with notable vulnerabilities.",
          "Monetary and fiscal regulatory bodies occasionally come under political pressure. Some national economic data may be incomplete or questionable.",
          "The government has a low investment-grade credit rating, and conditional access to international credit markets and the IMF / other international financial institutions.",
          "The country may have recently entered into a stand-by financing arrangement with the IMF or other bilateral lender.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "There is a low likelihood of default over the medium- to long-term. For example:",
        details: [
          "Government policy and ideology tolerate sustainable fiscal deficits and prioritise debt service as well as a stable currency. There are rare cases of non-payment.",
          "Central government debt is moderate; revenue and reserves are adequate; and the economy is stable with limited external vulnerabilities.",
          "Monetary and fiscal regulatory bodies operate relatively independently from political pressures and provide mostly credible and reliable economic data.",
          "The government has a high investment-grade credit rating, access to international credit markets, and good relations with the IMF and other international financial institutions.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "There is no significant likelihood of sovereign default. For example:",
        details: [
          "Government policy and ideology prioritise fiscal prudence, regular debt service and a stable currency.",
          "There is no recent precedent for default or non-payment.",
          "Central government debt is low, with strong revenue and reserves; and economic performance robust with no major external vulnerabilities.",
          "Monetary and fiscal regulatory bodies operate independently from political pressures and provide credible and reliable economic data. The government has a high credit rating, easy access to international credit markets, and good relations with the IMF and other international financial institutions.",
        ],
      },
    ],
  },
  {
    id: RISK_RATING_PRIMARIES.operational,
    name: "Operational Risk",
    description:
      "Operational risk evaluates the influence of societal and structural factors either facilitating or impeding efficient business operations. Factors assessed include infrastructure; ease of establishing and maintaining a functioning business; ease of recruiting and retaining skilled workers; and bureaucratic and business culture. Operational risk may vary for companies and investment projects because of factors such as industry sector, investor nationality and geographic location.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Operating conditions present sustained critical risks to business. For example:",
        details: [
          "Regulation is oppressive and hostile to foreign business. Alternatively, formal administration may be entirely lacking, rendering business operations extremely difficult.",
          "Infrastructure is severely deficient; businesses are likely to have to privately provision a significant portion of their infrastructure needs.",
          "Acute labour shortages and onerous regulation impose significant time and costs on business. Labour movements are hostile to foreign business.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "The operating environment presents persistent and serious challenges for business. For example:",
        details: [
          "Regulations are onerous and enforcement may be hostile to foreign business. Government administration is inadequately resourced and trained.",
          "Significant infrastructure deficiencies hinder business; companies may have to privately provision some of their infrastructure needs.",
          "Shortages of skilled labour in most sectors and complex regulation impose significant time and expense on business. Labour unrest may be frequent and highly disruptive.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Business faces regular operational difficulties; occasionally these may be serious. For example:",
        details: [
          "Complex regulation and bureaucratic inefficiency add to costs and cause delays. Institutions may be under-resourced or have overlapping remits.",
          "Some elements of infrastructure are expensive or inadequate.",
          "Labour shortages and regulation complicate operations in some areas. Labour unrest is common but generally localised.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "The environment is broadly positive with occasional challenges that do not significantly impede business. For example:",
        details: [
          "Official institutions are broadly efficient and mostly transparent, and regulations are mostly clear with some areas of complexity.",
          "Infrastructure is extensive and mostly efficient, but may be overstretched at times or in certain areas.",
          "Availability and regulation of labour imposes few additional costs on business; employment disputes are normally resolved through negotiation, with minimal disruption to business.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight: "The environment for business is favourable. For example:",
        details: [
          "Official institutions are efficient, well resourced and responsive to business. Regulations are clear and not onerous.",
          "Infrastructure is extensive, reliable and easy to access.",
          "Labour availability and regulations, and relations between unions and business are conducive to investment.",
        ],
      },
    ],
  },
  {
    id: "3a88683d-d4cc-46aa-992c-2b2116dc7682",
    name: "Infrastructure Risk",
    description:
      "Infrastructure risk evaluates the extent to which transport, power, water and communications networks facilitate or impede business operations. Factors assessed include the availability and quality of infrastructure in the jurisdiction; the government’s commitment and ability to maintain and/or improve infrastructure; and any significant constraints on infrastructure development, including inadequate finances, unfavourable geography, adverse land rights or pressure groups (including environmental, local community, indigenous rights, or other groups).",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Infrastructure is either unavailable or inadequate in most areas. For example:",
        details: [
          "Basic infrastructure is lacking in many areas, and generally insufficient and unreliable. Infrastructure is widely degraded due to conflict, natural disaster or other events. Businesses are required to privately provision a significant proportion of their infrastructure needs.",
          "The government does not currently prioritise infrastructure maintenance and development.",
          "Public organisational and financial capacities are severely limited, and generally incapable of supporting major infrastructure improvements. Opposition to infrastructure development is significant, and typically results in disruptive unrest or direct action.",
          "Levels of preparedness for and effectiveness of response to any likely natural disaster are minimal.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Infrastructure availability and quality are generally deficient, imposing significant constraints on business. For example:",
        details: [
          "Basic infrastructure is lacking in some areas, and frequently insufficient or unreliable. It may be degraded in places by conflict, natural disaster or other events. Businesses may need to privately provision a significant proportion of their infrastructure needs.",
          "The government notionally supports infrastructure development, but political challenges effectively prevent improvement or expansion of infrastructure.",
          "Public organisational and financial capacities are limited, resulting in routine delay to and frequent cancellation of infrastructure maintenance and improvements. Opposition to infrastructure development is significant in some areas, and often results in disruption or delay.",
          "Levels of preparedness for and effectiveness of response to any likely natural disaster are limited.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Infrastructure availability and quality are deficient in some areas, imposing some constraints on business. For example:",
        details: [
          "Basic infrastructure is extensive, but potentially insufficient or unreliable in some areas.",
          "The government broadly supports infrastructure development, but faces opposition in some areas.",
          "Public organisational or financial challenges cause regular delays to and sometimes cancellation of infrastructure maintenance and improvement. Development faces significant opposition in certain areas, and may face disruption or delay.",
          "Levels of preparedness for and effectiveness of response to any likely natural disaster are limited in some areas.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Infrastructure availability and quality are generally sufficient and impose few constraints on business. For example:",
        details: [
          "Basic infrastructure is extensive, accessible and generally sufficient, but occasionally unreliable.",
          "The government is mostly committed to expanding, maintaining and improving infrastructure and faces limited opposition.",
          "Public organisational or financial challenges cause occasional delay or interruption to infrastructure maintenance and improvement. Opposition to development is significant in certain areas, but generally resolved through clear legal and political processes.",
          "Levels of preparedness for and effectiveness of response to any likely natural disaster are mostly high.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "Infrastructure availability and quality are generally excellent and supportive of business activities. For example:",
        details: [
          "Basic infrastructure is extensive, easily accessible and reliable.",
          "The government is committed to expanding, maintaining and improving infrastructure in all areas as necessary to support social development and economic growth.",
          "Public institutions have high organisational and financial capacity to implement infrastructure projects. Opposition to development is limited and generally resolved through clear legal and political processes.",
          "Levels of preparedness for and effectiveness of response to any likely natural disaster are high.",
        ],
      },
    ],
  },
  {
    id: "f1f8d9ed-7bcb-4c6e-86bb-afbcafd711fc",
    name: "Institutional Risk",
    description:
      "Institutional risk evaluates the impact on business of the quality, efficiency and effectiveness of government administration and the judiciary. Factors assessed include the cost and complexity of compliance with business regulations; the prevalence and severity of regulatory bottlenecks; the ease of obtaining necessary licences, permits, visas and other required business documentation; and the efficiency and independence of the judiciary.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "State institutions pose sustained critical challenges to business. For example:",
        details: [
          "Regulation is so oppressive as to render normal business operations extremely difficult. Alternatively, lack of formal government or administration greatly complicates operations.",
          "Enforcement is unpredictable or hostile to foreign business",
          "Government administration may have severe capacity constraints, or overlapping and contested impose considerable costs and delays on most business operations.",
          "The legal system is essentially politicised, typically capricious, vulnerable to corruption and generally dysfunctional.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "State institutions present persistent and serious challenges for business. For example:",
        details: [
          "The volume, complexity and inconsistency of regulation at local and national levels significantly adds to costs and delays.",
          "Enforcement is inconsistent and may be hostile to foreign business.",
          "Government administration is inadequately resourced and trained, particularly outside the capital, imposing costs and delays on business.",
          "The legal system is subject to frequent political interference, potentially partial and capricious, and generally inefficient.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "State institutions pose occasionally serious challenges to business. For example:",
        details: [
          "The volume and complexity of regulation – including at local levels – adds undue costs and delays.",
          "Enforcement can be variable and opaque.",
          "Government administration may be under-resourced, particularly outside the capital. Overlapping remits between departments or between local and national institutions increase the cost and duration of navigating bureaucracy.",
          "The legal system is generally impartial and independent, but sometimes inefficient and vulnerable to sporadic political interference.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight: "State institutions rarely hinder business. For example:",
        details: [
          "The volume and complexity of regulation enables business to conduct operations largely without hindrance.",
          "Enforcement of regulations is broadly consistent, though there may be areas of complexity.",
          "Government bureaucracy is appropriately resourced, generally transparent, and usually responsive to business needs, but may be inefficient in some areas.",
          "The country has a generally efficient and impartial legal system, with judicial independence enshrined in the constitution and generally manifest in reality.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "State institutions generally facilitate operations. For example:",
        details: [
          "Levels of regulation are appropriate to ensure smooth business operations.",
          "Guidance on regulation is clear and enforcement is consistent and transparent.",
          "Government bureaucracy is well resourced, efficient and responsive to business needs.",
          "The country has an efficient and impartial legal system, with judicial independence enshrined in the constitution and manifest in reality.",
        ],
      },
    ],
  },
  {
    id: "086211a8-6c52-4277-ba7c-792d392bca12",
    name: "Labour Risk",
    description:
      "Labour risk evaluates the extent to which labour constraints and the (peaceful) activity of trade unions hinders business operations. Factors assessed include availability of skilled labour; local content laws and restrictions on hiring foreign workers; ease of obtaining necessary visas and work permits; the frequency and severity of strikes; and the prevalence of abuses such as forced or child labour.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Labour issues pose sustained critical risks to business. For example:",
        details: [
          "Acute shortages of skilled and/or unskilled labour severely constrain business operations.",
          "Companies are effectively unable to employ foreign workers due to restrictions.",
          "Employment legislation is onerous or incoherent; compliance imposes significant time and costs on business",
          "There are no effective routes for industrial dispute resolution; labour movements are hostile to foreign business and unrest is pervasive.",
          "Labour rights abuses are common.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Labour issues pose persistent and serious challenges for business. For example:",
        details: [
          "There are shortages of skilled labour in most sectors.",
          "There may be significant restrictions on hiring foreign workers or challenges obtaining documentation.",
          "Employment legislation is complex and can impose significant time and expense on business.",
          "Labour unrest may be frequent and highly disruptive; trade unions may be used by hostile political actors to disrupt business operations.",
          "Forced or child labour and other labour rights abuses occur in certain areas.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Labour issues pose occasionally serious challenges to business. For example:",
        details: [
          "There are shortages of skilled labour in some sectors.",
          "Companies face some restrictions on hiring foreign workers.",
          "Employment legislation can be complex in some areas; procedures for hiring or dismissing workers can impose costs and delays on business.",
          "Labour unrest is common but generally localised; trade unions may be politically influential and/or subject to government influence.",
          "There are occasional cases of forced or child labour, or other labour rights abuses.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "The labour environment is broadly favourable with occasional or low-level challenges to business. For example:",
        details: [
          "Skilled labour is widely available in most sectors.",
          "Companies face few barriers to importing foreign labour.",
          "Employment legislation is largely straightforward; procedures for hiring or dismissing workers do not impose undue costs on business.",
          "Employment disputes are normally resolved through negotiation, with minimal disruption to business.",
          "There is almost no prospect of exposure to forced or child labour, or labour rights abuse.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "The labour environment is favourable for business. For example:",
        details: [
          "Skilled labour is abundant.",
          "There are no significant restrictions affecting the hire of foreign workers.",
          "Employment regulation is clear; procedures for hiring or dismissing workers are straightforward and do not impose undue costs on business.",
          "Relations between trade unions and business are based on dialogue, and strikes are very rare.",
          "There is no prospect of exposure to forced or child labour, or labour rights abuse.",
        ],
      },
    ],
  },
  {
    id: RISK_RATING_PRIMARIES.security,
    name: "Security Risk",
    description:
      "Security risk evaluates threats to the financial, physical and human assets of a company, as well as the willingness and capability of public security forces to protect corporate assets and personnel. Factors assessed include military conflict, insurgency, terrorist attacks, strikes and riots, vandalism, kidnapping, and violent and acquisitive crime. Security risk may vary for companies and investment projects because of factors such as industry sector, investor nationality and geographic location.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Security conditions pose sustained critical risks to business; companies require significantly enhanced security provision to operate. For example:",
        details: [
          "Levels of acquisitive and violent crime are high and businesses are often targeted.",
          "Terrorist groups have high intent and capability to stage attacks, and may operate with impunity.",
          "Public disorder is routine and generally violent and/or destructive.",
          "The state faces acute national security threats and may be actively involved in conflict.",
          "Police and security services have severe capacity and/or capability deficits, or may be a direct source of threat to business.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "The security environment presents persistent and serious challenges for business; routine business activities generally require enhanced or specialised security provision. For example:",
        details: [
          "Acquisitive and/or violent crime, including by organised criminals, is a significant problem for business.",
          "Terrorist groups have significant intent and capability to stage attacks; large and small-scale attacks pose threats to business.",
          "Public disorder is frequent and often violent.",
          "The state may face significant internal or external national security threats with a high potential to cause harm to personnel or damage to business assets.",
          "Police and security services have significant capacity and/or capability deficits, or may be a source of threat to business.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Security conditions sometimes impede business operations; routine business activities may sometimes require enhanced security provision. For example:",
        details: [
          "Crime, including organised crime, poses occasionally serious threats to business.",
          "Terrorist groups have intent and capability to stage attacks; small-scale incidents may take place.",
          "Public disorder occurs periodically and is often characterised by violence and/or property destruction.",
          "The state may face internal or external national security threats with potential to affect business activities.",
          "Policing is generally sufficient to protect corporate assets from common crime, but security services may have capacity or capability deficits.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Security conditions do not impede normal business. For example:",
        details: [
          "Crime levels are low and crime mainly affects areas that do not impact on business.",
          "Terrorist groups have limited intent and capability to carry out attacks.",
          "Public disorder is uncommon and most demonstrations are peaceful.",
          "National security threats are manageable and do not pose an immediate security risk.",
          "Policing is broadly effective.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "The security environment for business is favourable. For example:",
        details: [
          "Crime levels are very low and crime has little impact on businesses or their personnel.",
          "Terrorist groups lack the intent and capability to stage attacks.",
          "Public disorder is rare and demonstrations are overwhelmingly peaceful.",
          "The state faces no significant internal or external national security threats.",
          "Policing is effective.",
        ],
      },
    ],
  },
  {
    id: "7b66b502-ecec-4e3b-832b-63642a7e72e9",
    name: "Crime Risk",
    description:
      "Crime risk evaluates the threat to business assets and personnel from offences against property (such as theft, burglary, fraud, embezzlement) and offences against the person (such as homicide, assault, sexual assault, robbery, hate crime). Factors assessed include levels and types of acquisitive, violent and organised crime, and the authorities’ ability to counter crime threats.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Crime poses sustained critical challenges to business. For example:",
        details: [
          "Levels of violent crime are very high and all areas are affected.",
          "Pervasive acquisitive crime causes severe disruption to business operations.",
          "Organised criminal groups operate largely unchecked and control key economic sectors with government complicity; legitimate business operations face significant pressure to make substantive concessions to criminal groups.",
          "Police and security forces are absent or complicit with criminal actors.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Crime presents persistent and serious challenges for business. For example:",
        details: [
          "Violent crime frequently affects areas used by businesses and their personnel.",
          "Property crime regularly targets business assets and personnel.",
          "Organised criminal groups control certain sectors and processes, and have some political cover for their activities. Predation on, or infiltration into, legitimate business operations is commonplace.",
          "Police and security forces are inadequate and may be complicit with criminal actors.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Crime poses occasionally serious challenges to business. For example:",
        details: [
          "Violent crime is mostly confined to certain areas but sometimes spills over into areas that affect business.",
          "Regular low-level or occasional high-impact acquisitive property crime may affect operations.",
          "Organised criminal groups engage in both licit and illicit business activities, and may exert leverage on legitimate business activities in certain sectors; criminal groups may have connections and influence with political elites in connection with specific issues.",
          "Policing is unreliable, and police and security forces may be under-resourced.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Crime poses infrequent or low-level challenges to business. For example:",
        details: [
          "Violent crime is rare and mainly occurs in areas that do not impact business.",
          "Acquisitive crime affects business and personnel infrequently.",
          "Organised criminal groups are present but their activities are unlikely to affect business.",
          "Police and security services are usually responsive, capable and well-resourced.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight: "Crime rarely affects business. For example:",
        details: [
          "Violent crime is very rare.",
          "Acquisitive crime levels are low and rarely affects business.",
          "Organised criminal groups are either absent or involved in only low-level activity that does not impinge on business operations.",
          "Police and security services are responsive, highly capable and well-resourced.",
        ],
      },
    ],
  },
  {
    id: "e0a13937-4947-410a-971c-287f15068d88",
    name: "Terrorism Risk",
    description:
      "Terrorism risk evaluates the capability and intent of groups and individuals to carry out acts of terrorism that may impact business operations. Factors assessed include intent to attack targets inside the country; the operational capabilities and likely target preferences of threat groups; and the ability of the authorities and security forces to counter terrorist threats. Terrorism risk may vary for companies and investment projects because of factors such as industry sector, investor nationality and geographic location.\nNote: Control Risks defines ‘terrorism’ as the calculated use of violence by non-state actors against state or civilian targets to advance an ideological cause by influencing a wider audience through intimidation. Terrorist acts include bombings, shootings, assassinations, hostage-taking, improvised or opportunistic attacks, and attacks using unconventional weapons.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Terrorism has sustained critical impacts on business. For example:",
        details: [
          "Domestic terrorist groups have high capability to carry out attacks against soft and hard targets, operate freely in major urban areas, and directly target business personnel and assets.",
          "Transnational terrorist groups have high intent and capability to carry out attacks, directly target business personnel and assets, and may be based within or adjacent to the jurisdiction.",
          "Domestic security and intelligence services are highly limited in terms of capability and/or capacity, allowing terrorist groups to operate with impunity within the jurisdiction.",
          "High-impact and/or sophisticated terrorist incidents are routine.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Terrorism has persistent, often direct, and serious impacts on business. For example:",
        details: [
          "Domestic terrorist groups have high intent and capability to carry out attacks against soft and hard targets, including in major urban areas, and occasionally directly target business personnel and assets.",
          "Transnational terrorist groups have significant intent and capability to carry out attacks, including against business personnel and assets, and may be based within or adjacent to the jurisdiction.",
          "Domestic security and intelligence service capacities or capabilities are generally insufficient to effectively mitigate the threat from either domestic or transnational terrorist groups.",
          "High-impact and/or sophisticated terrorist incidents may be regular, and small-scale or unsophisticated attacks common.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Terrorism has periodic but occasionally serious impacts on business, mostly indirect. For example:",
        details: [
          "Domestic terrorist groups have intent and capability to carry out low-level attacks that may cause limited business disruption.",
          "Transnational terrorist groups have intent and some capability to carry out occasional attacks that may affect business personnel and assets.",
          "Domestic security and intelligence services have sufficient capabilities in some geographic areas and tactics, but lack capacity or capability to effectively mitigate some domestic and transnational terrorism threats.",
          "High-impact and/or sophisticated terrorist incidents are infrequent, but small-scale or unsophisticated attacks sometimes occur.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Terrorism has occasional, typically indirect, impacts on business. For example:",
        details: [
          "Any domestic terrorist groups have limited capability to carry out attacks and low intent to stage attacks that would impact business assets and personnel.",
          "Transnational terrorist groups and affiliated individuals have low intent or capability to carry out attacks.",
          "Domestic security and intelligence services have sufficiently robust capacities and capabilities, and effectively mitigate most significant terrorism threats from both domestic and transnational groups.",
          "High-impact and/or sophisticated terrorist incidents are rare; small-scale or unsophisticated incidents are periodic.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight: "Terrorism rarely or never affects business. For example:",
        details: [
          "There are no known, active domestic terrorist groups.",
          "Transnational terrorist groups and affiliated individuals lack intent to carry out attacks and have very limited capability.",
          "Domestic security and intelligence services have sufficient capacities and capabilities to address any plausible threats.",
          "Terrorist incidents of any scale are extremely rare.",
        ],
      },
    ],
  },
  {
    id: "b0d04011-31c2-4c14-8339-cd7f7e9299a3",
    name: "War Risk",
    description:
      "War risk assesses the likelihood of state or organised non-state actors using force directly or indirectly affecting business operations. Factors assessed include the likelihood of inter-state war, civil war, revolution, rebellion, insurrection, insurgency, coup d’état, mutiny and counter-terrorism operations, and the associated potential for property damage, death and injury, and disruption to operations resulting from damage or denial of access to vital infrastructure.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Use of force by state or organised non-state actors poses sustained critical challenges to business. For example:",
        details: [
          "There is outright inter-state or civil war, insurgency, rebellion or state use of force for other domestic or inter-state purposes.",
          "No or negligible oversight of the security forces leads to regular arbitrary use of force.",
          "Business is at risk of severe and targeted asset damage and death or injury to personnel, as well as constant and large-scale disruption to commercial operations.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Use of force by state or organised non-state actors presents persistent and serious challenges for business. For example:",
        details: [
          "State or non-state forces are regularly involved in military, insurgent or other operations that affect business activities.",
          "There is weak oversight of security forces, which permits arbitrary use of force.",
          "Businesses are at risk of property damage and death or injury to their personnel, as well as frequent disruption in their operations.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Use of force by state or organised non-state actors is an occasionally serious challenge for business. For example:",
        details: [
          "State or organised non-state use of force tends to be infrequent but large in scale, or more regular but small in scale.",
          "Violence focuses on specific targets or geographical locations, rather than involving indiscriminate attacks.",
          "Actors stage attacks that cause occasional disruption to business operations, though property damage and personnel death or injuries are largely incidental rather than targeted, and limited in scale.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Use of force by state or organised non-state actors is rare and causes only infrequent or low-level challenges for business. For example:",
        details: [
          "The country is not at war or proximate to a war zone but may have unresolved territorial or border disputes with neighbours, or internal divisions.",
          "Oversight of state actors is mostly effective.",
          "Short-term business disruption is the most likely consequence of state or organised non-state use of force.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "State or organised non-state actors is extremely rare or never encountered and has little or no impact on business. For example:",
        details: [
          "There are no known issues likely to fuel an inter-state or domestic violent conflict.",
          "There is well-entrenched oversight and control over the security forces mitigating the risk of arbitrary use of force.",
          "Asset damage, death or injuries arising from use of force are highly unlikely.",
        ],
      },
    ],
  },
  {
    id: "688d8c7d-2bc0-4529-b720-0881824b0246",
    name: "Civil Unrest Risk",
    description:
      "Civil unrest risk (or SRCC) evaluates the likelihood of non-violent or violent mass actions by non-state actors disrupting routine social, government and business operations, as well as the severity of resultant damage or loss. Factors assessed include political, economic, labour, sectarian, communal and ethnic unrest and violence; the prevalence of strikes, demonstrations, protests, pickets or riots; and the level of associated property damage, death and injury, and disruption resulting from damage or denial of access to vital infrastructure.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Civil unrest poses sustained critical challenges to business. For example:",
        details: [
          "Unresolved and highly sensitive political, labour, communal, ethnic and related issues frequently cause violent civil unrest that often targets business.",
          "The state lacks intent or adequate capability to control and limit unrest.",
          "Business is at risk of severe asset damage and death or injury to personnel, as well as frequent and large-scale disruption in commercial operations.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Civil unrest presents persistent and serious challenges for business. For example:",
        details: [
          "Strikes, riots and protests are frequent and often violent.",
          "Organised violence against minority groups is common.",
          "Protest groups may wield significant formal or informal influence over state entities, or the state has limited intent or capability to control or restrict social unrest.",
          "Business is at risk of property damage and death or injury to personnel, as well as frequent disruption in operations.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Civil unrest poses occasionally serious challenges to business. For example:",
        details: [
          "Protests and strikes occur frequently, but are largely peaceful.",
          "Violence associated with protests and strikes is short-lived and uncommon.",
          "Communal tensions are generally localised and temporary.",
          "Civil unrest causes occasional disruption to business operations, though property damage and injuries are mostly incidental rather than targeted, and limited in scale.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Civil unrest generally causes infrequent or low-level impacts on to business. For example:",
        details: [
          "Communal tension is generally short-lived and rarely leads to violence.",
          "Social and economic protests occur occasionally, but protest groups operate largely peacefully and within the law.",
          "Short-term business disruption or occasional low-level vandalism is the most likely consequence of social unrest.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "Civil unrest rarely or never affects business. For example:",
        details: [
          "There are few sensitive political, labour, communal, ethnic or related issues that would drive protest.",
          "Protests are typically limited in duration and scope, and remain peaceful.",
          "There are well-entrenched political, security and social restraints on violent unrest.",
          "Asset damage, death or injuries arising from social unrest are highly unlikely.",
        ],
      },
    ],
  },
  {
    id: "8b3def4a-44e3-473a-9cc9-79a2e2539311",
    name: "Kidnap Risk",
    description:
      "Kidnap risk evaluates the likelihood of the abduction of an employee or their family member with the intent of detaining them in an unknown location until a demand is met. Factors assessed include the intent and capability of kidnappers to target business personnel, including local and foreign nationals, and the authorities’ ability to counter and respond to the threat. It also assesses the impact of the crime in terms of case duration and likely outcome.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Kidnapping poses sustained critical challenges to businesses and individuals. For example:",
        details: [
          "Kidnaps are widespread and kidnappers operate with impunity.",
          "Criminals, including highly organised kidnapping groups, have high capability and intent to target business personnel or their families.",
          "Domestic and/or transnational terrorist groups operate freely. They have high intent and capability to kidnap business personnel or their families, and the threat to foreigners is acute.",
          "Kidnaps are often high impact in terms of case duration and/or outcome.",
          "Police and security forces are absent or complicit with criminal actors.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Kidnapping presents a persistent and serious challenge for businesses and personnel. For example:",
        details: [
          "Kidnaps occur frequently and kidnappers regularly target business personnel or their families.",
          "Criminals, including organised kidnapping groups, frequently use kidnapping as a tactic and have significant capability and intent to target business personnel or their families.",
          "Terrorist/militant groups use kidnapping frequently and occasionally target business personnel, especially foreigners.",
          "Kidnaps have the potential to be high impact in terms of case duration and/or outcome.",
          "Police and security forces are inadequate and may be complicit with criminal actors.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Kidnapping presents potentially serious challenges to businesses and their personnel. For example:",
        details: [
          "Kidnaps may occur regularly and kidnappers occasionally target business personnel or their families.",
          "Criminals often use kidnapping as a tactic and some have the capability and intent to target business personnel or their families.",
          "Terrorists/militants use kidnapping infrequently as a tactic or would be unlikely to target business personnel.",
          "Kidnaps are unlikely to be high impact in terms of case duration and/or outcome.",
          "Policing is unreliable, and police and security forces may be under-resourced.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Kidnapping has infrequent or low-level impacts on businesses and their personnel. For example:",
        details: [
          "Kidnaps are unusual and kidnappers are unlikely to target business personnel or their families.",
          "Criminal groups use kidnapping infrequently as a tactic, predominantly in inter-gang rivalry or to recover debts.",
          "Terrorist/militant groups have little presence or are unlikely to use kidnapping as a tactic.",
          "Kidnaps are rarely high impact in terms of case duration and/or outcome.",
          "Police and security services are usually responsive, capable and well-resourced.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "Kidnapping rarely or never affects businesses and their personnel. For example:",
        details: [
          "Kidnaps are extremely rare and kidnappers are highly unlikely to target business personnel or their families.",
          "Criminal groups are not known to use kidnapping as a tactic.",
          "Terrorist/militant groups are absent or do not use kidnapping as a tactic.",
          "Kidnaps are very rarely high impact in terms of case duration and/or outcome.",
          "Police and security services are responsive, highly capable and well-resourced.",
        ],
      },
    ],
  },
  {
    id: RISK_RATING_PRIMARIES.cyber,
    name: "Cyber Risk",
    description:
      "Our cyber ratings evaluate the likelihood of cyber threat actors affecting an organisation's operations in, or remote workers visiting, a given country. These ratings consider the overall capabilities and intent of state-sponsored threat actors, cybercriminals and cyber activists operating in or targeting the country. Although each country's rating refers to the overall threat of significant cyber attacks within that country, it can reflect the predominance of a particular category of threat actor over others, and does not entail that all state-sponsored, cybercriminal and cyber activist groups possess the same level of capabilities and intent. When one of these categories of threat actor has a disproportionate weight in determining the country's rating, this will be detailed in the country profile.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Cyber threats pose sustained and critical challenges to business. For example:",
        details: [
          "The country’s military and intelligence apparatus possesses very sophisticated cyber offensive capabilities and routinely uses them against domestic targets.",
          "The country is among the most targeted by very sophisticated computer network operations by foreign state-sponsored threat actors.",
          "Cybercriminals have significant technical expertise and access to sophisticated attack tools; attacks are routinely prolonged and targeted.",
          "Numerous cyber activist groups are highly organised and have technical expertise, and often conduct campaigns against domestic targets.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Cyber threats pose persistent and serious challenges for business. For example:",
        details: [
          "The country’s military and intelligence apparatus possesses sophisticated cyber offensive capabilities and has used them against domestic targets.",
          "Foreign state-sponsored threat actors have often conducted computer network operations in the country.",
          "Cybercriminals have significant technical expertise and access to attack tools; attacks can be prolonged and targeted.",
          "Cyber activist groups have a high degree of organisation and have conducted campaigns against numerous domestic targets.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Cyber threats pose occasionally serious challenges to business. For example:",
        details: [
          "The country’s military and intelligence apparatus possesses limited cyber offensive capabilities and has occasionally used them against domestic targets.",
          "Foreign state-sponsored threat actors have occasionally conducted computer network operations in the country.",
          "Cybercriminals have some technical expertise and access to attack tools, but incidents remain low-level and untargeted.",
          "Cyber activist groups have a degree of organisation and have conducted some low-level campaigns against domestic targets.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Cyber threats pose infrequent or low-level challenges to business. For example:",
        details: [
          "The country’s military and intelligence apparatus does not possess significant cyber offensive capabilities or has not used them extensively against domestic targets.",
          "Foreign state-sponsored threat actors have not conducted significant computer network operations in the country.",
          "There is no significant cybercrime affecting the country.",
          "Cyber activist groups have not conducted significant attacks against organisations in the country.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight: "Cyber threats rarely affect business. For example:",
        details: [
          "The country’s military and intelligence apparatus does not possess cyber offensive capabilities or has not used them against domestic targets.",
          "Foreign state-sponsored threat actors have not targeted organisations in the country.",
          "There are no recorded cybercriminal incidents in the country.",
          "There are no domestic cyber activist groups.",
        ],
      },
    ],
  },
  {
    id: "6228fe95-09d0-4fc8-a64c-8b2986061541",
    name: "Cyber Extortion Risk",
    description:
      "Cyber extortion risk assesses the likelihood of financially motivated threat actors seeking to coerce organisations into making a payment, such as by limiting or threatening to limit access to systems or services. Examples of such attacks include data-encrypting ransomware, distributed denial of service (DDoS) for extortion or data leak for extortion.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Cyber extortion risk poses sustained and critical challenges to business. For example, domestic or international cybercriminals have significant technical expertise or ready access to sophisticated tools, and often use these to target organisations in the country, in attacks that result in significant losses for victim organisations.",
        details: [],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Cyber extortion risk poses persistent and serious challenges to business. For example, domestic or international cybercriminals have significant technical expertise or ready access to sophisticated tools, and can use these to target organisations in the country, in attacks that may result in significant losses for victim organisations.",
        details: [],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Cyber extortion risk poses occasionally serious challenges to business. For example, domestic or international cybercriminals have some technical expertise or access to tools, and can use these to target organisations in the country, in opportunistic attacks that may result in some losses for victim organisations.",
        details: [],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Cyber extortion risk poses infrequent or low-level challenges to business. Domestic cybercriminals have little technical expertise and limited access to tools, and are unlikely to target organisations in the country.",
        details: [],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "Cyber extortion risk rarely affects business. Cybercriminals are highly unlikely to target organisations in the country.",
        details: [],
      },
    ],
  },
  {
    id: "e948a40d-0bc7-4376-9401-8c8afc0fa716",
    name: "Cyber Data Leak/Defacement/DDos Risk",
    description:
      "This risk assesses the likelihood of low-level threat actors, motivated by a range of reasons including political goals or for their own entertainment, targeting organisations' outward-facing assets to attract public attention. Examples include distributed denial of service (DDoS) attacks, website defacements, social media hijacking, and leaking and publishing data intended to embarrass the targeted organisation.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "This risk poses sustained and critical challenges to business. For example, numerous cyber activist groups are highly organised and have technical expertise, and often conduct such campaigns against domestic targets.",
        details: [],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "This risk poses persistent and serious challenges for business. For example, cyber activist groups have a high degree of organisation and have conducted such campaigns against numerous domestic targets.",
        details: [],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "This risk poses occasionally serious challenges to business. For example, cyber activist groups have a degree of organisation and have conducted some low-level campaigns of this nature against domestic targets.",
        details: [],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "This risk poses infrequent or low-level challenges to business. For example, cyber activist groups have not conducted significant attacks of this nature against organisations in the country.",
        details: [],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "This risk rarely affects business. For example, there are no domestic cyber activist groups carrying out such campaigns in the country.",
        details: [],
      },
    ],
  },
  {
    id: "0c5b9cad-8dc1-46f9-9ee8-23b59616938a",
    name: "Cyber Disruptive Attack Risk",
    description:
      "Disruptive attack risk assesses the likelihood of state-sponsored cyber threat actors conducting computer network compromises aimed at deleting data or permanently hindering access to a target's systems or services. This type of risk can refer either to highly targeted campaigns against specific organisations, or to wider campaigns against multiple organisations with similar characteristics, such as geography or sector.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Disruptive cyber attacks pose sustained and critical challenges to business. The country is among the most targeted by computer network attacks by foreign state-sponsored threat actors with very sophisticated capabilities.",
        details: [],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Disruptive cyber attacks pose persistent and serious challenges to business. Foreign state-sponsored threat actors with sophisticated capabilities have often conducted computer network attacks in the country.",
        details: [],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Disruptive cyber attacks pose occasionally serious challenges to business. Foreign state-sponsored threat actors have occasionally conducted computer network attacks in the country.",
        details: [],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Disruptive cyber attacks pose infrequent or low-level challenges to business. Foreign state-sponsored threat actors have not conducted significant computer network attacks in the country.",
        details: [],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "Disruptive cyber attacks rarely affect business. Foreign state-sponsored threat actors have not conducted computer network attacks in the country.",
        details: [],
      },
    ],
  },
  {
    id: "44544fb9-1439-4aad-bceb-a6c9cb9602bb",
    name: "Cyber Espionage Risk",
    description:
      "Cyber espionage risk assesses the likelihood of state-sponsored cyber threat actors conducting prolonged and highly targeted computer network compromises aimed at exfiltrating sensitive data for political, military or commercial purposes.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Cyber espionage poses sustained and critical challenges to business. For example, the country’s military and intelligence apparatus possesses very sophisticated cyber offensive capabilities and routinely uses them against domestic targets; and/or the country is among the most targeted by very sophisticated computer network exploitations by foreign state-sponsored threat actors.",
        details: [],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Cyber espionage poses persistent and serious challenges for business. For example, the country’s military and intelligence apparatus possesses sophisticated cyber offensive capabilities and has used them against domestic targets; and/or foreign state-sponsored threat actors have often conducted sophisticated computer network exploitations in the country.",
        details: [],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Cyber espionage poses occasionally serious challenges to business. For example, the country’s military and intelligence apparatus possesses limited cyber offensive capabilities and has occasionally used them against domestic targets; and/or foreign state-sponsored threat actors have occasionally conducted computer network exploitations in the country.",
        details: [],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Cyber espionage poses infrequent or low-level challenges to business. For example, the country’s military and intelligence apparatus does not possess significant cyber offensive capabilities or has not used them extensively against domestic targets; and/or foreign state-sponsored threat actors have not conducted significant computer network exploitations in the country.",
        details: [],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "Cyber espionage rarely affects business. For example, the country’s military and intelligence apparatus does not possess cyber offensive capabilities or has not used them against domestic targets; and/or foreign state-sponsored threat actors have not targeted organisations in the country.",
        details: [],
      },
    ],
  },
  {
    id: "42b7a9e7-cd08-4e7e-ab5d-06c2d7af6c77",
    name: "Cyber Financial Fraud Risk",
    description:
      "Financial fraud risk assesses the likelihood of financially motivated threat actors conducting a wide range of cyber-enabled operations aimed at illicit transfers of funds or at stealing data for resale. Examples include data breaches aimed at stealing personal or financial information, payment diversion fraud through targeted network intrusions or business email compromise/CEO fraud.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "Cyber-enabled financial fraud risk poses sustained and critical challenges to business. For example, domestic or international cybercriminals have significant technical expertise or social engineering skills, and often use these to target organisations in the country, in attacks that result in significant losses for victim organisations.",
        details: [],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Cyber-enabled financial fraud risk poses persistent and serious challenges to business. For example, domestic or international cybercriminals have significant technical expertise or social engineering skills, and can use these to target organisations in the country, in attacks that may result in significant losses for victim organisations.",
        details: [],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Cyber-enabled financial fraud risk poses occasionally serious challenges to business. For example, domestic cybercriminals have some technical expertise or social engineering skills, and can use these to target organisations in the country, in opportunistic attacks that may result in some losses for victim organisations.",
        details: [],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Cyber-enabled financial fraud risk poses infrequent or low-level challenges to business. Domestic cybercriminals have little technical expertise and a low level of social engineering skills, and are unlikely to target organisations in the country.",
        details: [],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "Cyber-enabled financial fraud risk rarely affects business. Cybercriminals are highly unlikely to target organisations in the country.",
        details: [],
      },
    ],
  },
  {
    id: RISK_RATING_PRIMARIES.maritime,
    name: "Maritime Risk",
    description:
      "Maritime risk evaluates threats to the financial, physical and human assets of an organisation while operating offshore, on inland waterways or alongside a sea port, as well as the willingness and capability of public security forces to protect assets and personnel. Factors assessed include military conflict, insurgency, terrorism, kidnapping of crew from vessels, hijacking of vessels, violent and acquisitive crime, activism, human migration and trafficking, and smuggling of illicit goods. Maritime risk may vary for companies and projects because of factors such as vessel type, national associations (such as the flag under which the vessel is registered) and type of cargo.",
    levels: [
      {
        name: "EXTREME",
        value: 5,
        highlight:
          "The environment contains threats that pose sustained critical challenges to maritime operations and businesses using maritime operators in the area. For example:",
        details: [
          "There is outright inter-state or civil war, insurgency, rebellion or state use of force for other domestic or inter-state purposes that impacts the maritime domain.",
          "Maritime kidnaps are widespread and kidnappers operate with impunity.",
          "Levels of maritime crime are very high and all areas are affected.",
        ],
      },
      {
        name: "HIGH",
        value: 4,
        highlight:
          "Security threats pose a persistent and serious challenges for maritime operations and business. For example:",
        details: [
          "State or non-state forces are regularly involved in military, insurgent or other operations that affect maritime activities.",
          "Maritime kidnaps occur frequently and kidnappers regularly target maritime assets and people on board.",
          "Violent crime frequently affects vessels with crew regularly encountering thieves armed with weapons.",
        ],
      },
      {
        name: "MEDIUM",
        value: 3,
        highlight:
          "Security threats pose occasionally serious challenges for maritime operations and business. For example:",
        details: [
          "State or organised non-state use of force that affects maritime activities tends to be infrequent but large in scale, or more regular but small in scale.",
          "Maritime kidnaps may occur regularly and kidnappers occasionally target maritime assets and people on board.",
          "Violent crime is mostly confined to certain ports and anchorages.",
        ],
      },
      {
        name: "LOW",
        value: 2,
        highlight:
          "Security threats are rare and cause only infrequent or low-level challenges for maritime operations and business. For example:",
        details: [
          "The country is not at war or proximate to a war zone but may have unresolved territorial or border disputes with neighbours, or internal divisions, with the potential to spark limited conflict.",
          "Maritime kidnaps are unusual and kidnappers are unlikely to target maritime operations and crew.",
          "Violent crime aboard vessels is rare; violent crime mainly occurs on land rather than on vessels but has the potential to proliferate into offshore criminality.",
        ],
      },
      {
        name: "VERY LOW",
        value: 1,
        highlight:
          "Security threats are rare and cause only infrequent or low-level challenges for maritime operations and business. For example:",
        details: [
          "There are no known issues likely to fuel an inter-state or domestic violent conflict.",
          "Maritime kidnaps are extremely rare and kidnappers are highly unlikely to target maritime operations and crew.",
          "Violent crime aboard vessels is very rare and there is no established violent crime trend onshore that could proliferate to vessels offshore or at ports and anchorages.",
        ],
      },
    ],
  },
];
